/**
 * AICaseGenerator — Generación de casos con IA real.
 *
 * Flujo:
 * 1. Usuario escribe contexto y/o sube capturas
 * 2. POST /ai/generate-cases → backend llama al LLM
 * 3. Casos generados se muestran con pasos expandibles
 * 4. Usuario selecciona cuáles guardar → POST /requirements/{id}/cases
 * 5. Feedback (aceptado/rechazado) se envía al backend para auditoría
 */
import React, { useState, useRef, useCallback } from 'react';
import { useShallow } from 'zustand/react/shallow';
import { ai as aiApi, cases as casesApi } from '../../services/api';
import { useAppStore } from '../../store';
import type { TestCase, AIGenerationResult, TestSystem } from '../../types';

interface AICaseGeneratorProps {
  scenarioId: string;
  scenarioTitle: string;
  system?: TestSystem;
  onCasesCreated: (newCases: TestCase[]) => void;
}

type GenerationMode = 'text' | 'screenshot' | 'mixed';

interface PreviewFile {
  file: File;
  previewUrl: string;
}

interface GeneratedStep {
  description: string;
  expected_result: string;
  actions: { type: string; target_selector?: string; value?: string; description: string }[];
}

interface GeneratedCase {
  title: string;
  description: string;
  tags: string[];
  status: string;
  criticality?: string;
  preconditions?: string;
  postconditions?: string;
  steps?: GeneratedStep[];
}

// El backend antepone "[categoria] mensaje" al detail cuando la generación
// falla (ver AI_ERR_* en backend/app/api/routers/ai.py) para poder distinguir
// falta de créditos, key inválida, límite de tasa, red intermitente, etc. en
// vez de un 502 genérico sin causa.
const AI_ERROR_MESSAGES: Record<string, string> = {
  configuracion_invalida: 'No hay API key de IA configurada. Ve a Ajustes → Inteligencia Artificial para configurarla.',
  api_key_invalida: 'El proveedor de IA rechazó la API key configurada (inválida, revocada o sin permisos). Revísala en Ajustes → Inteligencia Artificial.',
  sin_creditos: 'La cuenta del proveedor de IA no tiene créditos/saldo suficiente para generar los casos.',
  limite_de_tasa: 'Se alcanzó el límite de solicitudes del proveedor de IA (rate limit). Espera unos segundos e intenta de nuevo.',
  solicitud_invalida: 'El proveedor de IA rechazó la solicitud (revisa el tamaño/formato de las capturas o el contexto enviado).',
  servicio_no_disponible: 'El servicio de IA no está disponible en este momento. Intenta de nuevo en unos minutos.',
  red_intermitente: 'No se pudo conectar con el servicio de IA (red intermitente). Intenta de nuevo.',
  timeout: 'La IA tardó demasiado en responder. Intenta con menos capturas o texto más corto.',
  respuesta_truncada: 'La respuesta del modelo se cortó por límite de tokens antes de terminar. Intenta con menos contexto o pide menos casos.',
  respuesta_no_valida: 'El modelo no devolvió una respuesta interpretable. Intenta de nuevo o ajusta el contexto.',
  sin_casos_generados: 'El modelo no generó casos válidos. Intenta con más contexto.',
};

export function AICaseGenerator({ scenarioId, scenarioTitle, system, onCasesCreated }: AICaseGeneratorProps) {
  const [systemExpanded, setSystemExpanded] = useState(false);
  const [mode, setMode] = useState<GenerationMode>('text');
  const [contextText, setContextText] = useState('');
  const [contextMode, setContextMode] = useState<'guide' | 'free'>('free');
  const [guide, setGuide] = useState({
    rol: '', accion: '', beneficio: '',
    criterios: [''], reglas: [''], flujos: [''], notas: '',
  });
  const serializeGuide = (g = guide): string => {
    const lines: string[] = [];
    if (g.rol) lines.push(`Como: ${g.rol}`);
    if (g.accion) lines.push(`Quiero: ${g.accion}`);
    if (g.beneficio) lines.push(`Para: ${g.beneficio}`);
    const crit = g.criterios.filter(Boolean);
    if (crit.length) { lines.push(''); lines.push('CRITERIOS DE ACEPTACIÓN:'); crit.forEach(c => lines.push(`- ${c}`)); }
    const reg = g.reglas.filter(Boolean);
    if (reg.length) { lines.push(''); lines.push('REGLAS DE NEGOCIO:'); reg.forEach(r => lines.push(`- ${r}`)); }
    const flu = g.flujos.filter(Boolean);
    if (flu.length) { lines.push(''); lines.push('FLUJOS ALTERNATIVOS / EXCEPCIONES:'); flu.forEach(f => lines.push(`- ${f}`)); }
    if (g.notas.trim()) { lines.push(''); lines.push('NOTAS TÉCNICAS:'); lines.push(g.notas.trim()); }
    return lines.join('\n');
  };
  const getContextText = () => contextMode === 'guide' ? serializeGuide() : contextText;
  const updateGuideList = (field: 'criterios' | 'reglas' | 'flujos', idx: number, val: string) =>
    setGuide(prev => { const list = [...prev[field]]; list[idx] = val; return { ...prev, [field]: list }; });
  const addGuideItem = (field: 'criterios' | 'reglas' | 'flujos') =>
    setGuide(prev => ({ ...prev, [field]: [...prev[field], ''] }));
  const removeGuideItem = (field: 'criterios' | 'reglas' | 'flujos', idx: number) =>
    setGuide(prev => ({ ...prev, [field]: prev[field].filter((_, i) => i !== idx) }));
  const [screenshots, setScreenshots] = useState<PreviewFile[]>([]);
  const [isDraggingOver, setIsDraggingOver] = useState(false);
  const [complementing, setComplementing] = useState(false);
  const [complementModal, setComplementModal] = useState<string | null>(null);
  const [complementEdited, setComplementEdited] = useState('');
  const [result, setResult] = useState<AIGenerationResult | null>(null);
  const [maxCases, setMaxCases] = useState(15);
  const [selectedCases, setSelectedCases] = useState<Set<number>>(new Set());
  const [expandedCase, setExpandedCase] = useState<number | null>(null);
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const fileInputRef = useRef<HTMLInputElement>(null);

  const { setAIGenerating, addNotification } = useAppStore(
    useShallow(s => ({ setAIGenerating: s.setAIGenerating, addNotification: s.addNotification }))
  );

  // ── File handling ────────────────────────────────────────────────────────

  const addFiles = useCallback((files: FileList | File[]) => {
    const imageFiles = Array.from(files).filter(f => f.type.startsWith('image/'));
    const newPreviews = imageFiles.map(f => ({ file: f, previewUrl: URL.createObjectURL(f) }));
    setScreenshots(prev => [...prev, ...newPreviews].slice(0, 8));
  }, []);

  const handleDrop = useCallback((e: React.DragEvent) => {
    e.preventDefault();
    setIsDraggingOver(false);
    addFiles(e.dataTransfer.files);
  }, [addFiles]);

  const removeScreenshot = (idx: number) => {
    setScreenshots(prev => {
      URL.revokeObjectURL(prev[idx].previewUrl);
      return prev.filter((_, i) => i !== idx);
    });
  };

  // ── Generation ───────────────────────────────────────────────────────────

  const handleComplement = async () => {
    if (!contextText.trim()) return;
    setComplementing(true);
    try {
      const data = await aiApi.complementContext({
        scenario_id: scenarioId,
        scenario_title: scenarioTitle,
        context_text: contextText,
      });
      setComplementModal(data.complemented_text);
      setComplementEdited(data.complemented_text);
    } catch (e) {
      console.error('Complement error:', e);
    } finally {
      setComplementing(false);
    }
  };

  const generate = async () => {
    setError(null);
    setLoading(true);
    setAIGenerating(true);
    setResult(null);
    setExpandedCase(null);

    try {
      const res = await aiApi.generateCases({
        scenario_id: scenarioId,
        context_text: getContextText().trim() || undefined,
        screenshots: screenshots.map(s => s.file),
        generation_type: mode === 'screenshot' ? 'from_screenshot'
          : mode === 'mixed' ? 'mixed' : 'from_text',
        case_count: maxCases,
      });
      setResult(res);
      setSelectedCases(new Set(res.cases.map((_, i) => i)));
      if (res.cases.length > 0) setExpandedCase(0);
      if (res.truncated) {
        addNotification({
          type: 'warning',
          title: 'Respuesta de IA incompleta',
          message: 'El modelo se quedó sin espacio de tokens antes de terminar. Se recuperaron los casos que alcanzaron a completarse — revisa si falta cobertura antes de guardarlos.',
        });
      }
    } catch (e: unknown) {
      const msg = (e as Error).message ?? 'Error generando casos';
      const match = msg.match(/^\[([a-z_]+)\]\s*(.*)$/);
      setError(match ? (AI_ERROR_MESSAGES[match[1]] || match[2]) : msg);
    } finally {
      setLoading(false);
      setAIGenerating(false);
    }
  };

  // ── Save selected cases (atomic: cases + steps + actions) ──────────────

  const saveSelected = async () => {
    if (!result || selectedCases.size === 0) return;
    setSaving(true);
    try {
      const toCreate = (result.cases as GeneratedCase[]).filter((_, i) => selectedCases.has(i));

      // Single atomic call — backend persists cases + steps + DSL actions in one transaction
      const token = localStorage.getItem('access_token');
      const res = await fetch('/api/v1/ai/save-cases', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${token}`,
        },
        body: JSON.stringify({
          scenario_id: scenarioId,
          generation_id: result.generation_id || null,
          cases: toCreate.map(c => ({
            title: c.title,
            description: c.description || '',
            tags: c.tags,
            criticality: c.criticality || 'medium',
            preconditions: c.preconditions || undefined,
            postconditions: c.postconditions || undefined,
            steps: (c.steps ?? []).map(s => ({
              description: s.description,
              expected_result: s.expected_result || '',
              actions: (s.actions ?? []).map(a => ({
                type: a.type,
                target_selector: a.target_selector || null,
                value: a.value || null,
                description: a.description || '',
              })),
            })),
          })),
        }),
      });

      if (!res.ok) {
        const err = await res.json().catch(() => ({}));
        throw new Error(err.detail || err.message || `Error ${res.status}`);
      }

      const saveResult = await res.json();

      // Build minimal TestCase objects for the parent component
      const created = saveResult.saved.map((s: { id: string; title: string }) => ({
        id: s.id,
        title: s.title,
        scenario_id: scenarioId,
        status: 'draft' as const,
        generated_by: 'ai' as const,
        tags: [],
        version: 1,
        order_index: 0,
        created_at: new Date().toISOString(),
        updated_at: new Date().toISOString(),
      }));

      onCasesCreated(created);
      addNotification({
        type: 'success',
        title: `${saveResult.total_cases} caso(s) creados`,
        message: `${saveResult.total_steps} pasos y ${saveResult.total_actions} acciones DSL generadas.`,
      });
      setResult(null);
      setContextText('');
      setScreenshots([]);
    } catch (e: unknown) {
      addNotification({
        type: 'error',
        title: 'Error guardando casos',
        message: (e as Error).message,
      });
    } finally {
      setSaving(false);
    }
  };

  // ── UI helpers ───────────────────────────────────────────────────────────

  const ModeBtn = ({ m, label, icon }: { m: GenerationMode; label: string; icon: string }) => (
    <button onClick={() => setMode(m)} style={{
      flex: 1, padding: '10px 8px', borderRadius: 10, cursor: 'pointer',
      border: `1px solid ${mode === m ? '#7F77DD' : 'var(--color-border-secondary)'}`,
      background: mode === m ? '#EEEDFE' : 'transparent',
      color: mode === m ? '#3C3489' : 'var(--color-text-secondary)',
      fontSize: 12, fontWeight: mode === m ? 600 : 400,
      display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 4, transition: 'all 0.15s',
    }}>
      <span style={{ fontSize: 18 }}>{icon}</span>
      {label}
    </button>
  );

  // ── Render ───────────────────────────────────────────────────────────────

  return (
    <div style={{ borderRadius: 16, border: '1px solid var(--color-border-secondary)', background: 'var(--color-background-primary)', overflow: 'hidden' }}>
      {/* Header */}
      <div style={{ padding: '14px 18px', background: '#EEEDFE', borderBottom: '1px solid #CECBF6', display: 'flex', alignItems: 'center', gap: 10 }}>
        <span style={{ fontSize: 18 }}>✦</span>
        <div>
          <div style={{ fontSize: 14, fontWeight: 700, color: '#26215C' }}>Generar casos con IA</div>
          <div style={{ fontSize: 11, color: '#534AB7' }}>{scenarioTitle}</div>
        </div>
        {result && (
          <div style={{ marginLeft: 'auto', fontSize: 10, color: '#534AB7', background: '#EEEDFE', border: '1px solid #CECBF6', borderRadius: 6, padding: '2px 8px' }}>
            {result.model_used} · {result.tokens_used} tokens
          </div>
        )}
      </div>

      <div style={{ padding: 18 }}>
        {!result ? (
          <>
            {/* Mode selector */}
            <div style={{ display: 'flex', gap: 8, marginBottom: 16 }}>
              <ModeBtn m="text" label="Descripción" icon="📝" />
              <ModeBtn m="screenshot" label="Capturas" icon="🖼️" />
              <ModeBtn m="mixed" label="Combinado" icon="⚡" />
            </div>

            {/* Text input */}
            {(mode === 'text' || mode === 'mixed') && (
              <div style={{ marginBottom: 14, position: 'relative' }}>
                {/* Context mode toggle */}
                <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 8 }}>
                  <label style={{ fontSize: 11, fontWeight: 700, color: 'var(--color-text-secondary)', textTransform: 'uppercase', letterSpacing: '0.05em' }}>
                    Contexto / Historia de Usuario
                  </label>
                  <div style={{ display: 'flex', gap: 0, borderRadius: 6, overflow: 'hidden', border: '1px solid var(--color-border-secondary)' }}>
                    <button onClick={() => setContextMode('guide')}
                      style={{ padding: '3px 10px', border: 'none', background: contextMode === 'guide' ? '#3B8BD4' : 'transparent', color: contextMode === 'guide' ? '#fff' : 'var(--color-text-secondary)', fontSize: 10, fontWeight: 600, cursor: 'pointer' }}>
                      📋 Guía
                    </button>
                    <button onClick={() => setContextMode('free')}
                      style={{ padding: '3px 10px', border: 'none', background: contextMode === 'free' ? '#3B8BD4' : 'transparent', color: contextMode === 'free' ? '#fff' : 'var(--color-text-secondary)', fontSize: 10, fontWeight: 600, cursor: 'pointer' }}>
                      ✎ Libre
                    </button>
                  </div>
                </div>

                {contextMode === 'guide' ? (
                  <div style={{ display: 'flex', flexDirection: 'column', gap: 10, padding: '12px 14px', borderRadius: 10, border: '1px solid var(--color-border-secondary)', background: 'var(--color-background-secondary)' }}>

                    {/* Como / Quiero / Para */}
                    {([
                      { key: 'rol', label: 'Como...', placeholder: 'usuario registrado, administrador, visitante anónimo...' },
                      { key: 'accion', label: 'Quiero...', placeholder: 'iniciar sesión con email y contraseña...' },
                      { key: 'beneficio', label: 'Para...', placeholder: 'acceder a mi cuenta y ver mi historial de pedidos...' },
                    ] as const).map(f => (
                      <div key={f.key} style={{ display: 'flex', gap: 8, alignItems: 'flex-start' }}>
                        <span style={{ fontSize: 11, fontWeight: 700, color: '#3B8BD4', minWidth: 60, paddingTop: 9, flexShrink: 0 }}>{f.label}</span>
                        <input
                          value={guide[f.key]}
                          onChange={e => setGuide(prev => ({ ...prev, [f.key]: e.target.value }))}
                          placeholder={f.placeholder}
                          style={{ flex: 1, padding: '8px 10px', borderRadius: 8, border: '1px solid var(--color-border-secondary)', background: 'var(--color-background-primary)', color: 'var(--color-text-primary)', fontSize: 12, outline: 'none', fontFamily: 'inherit' }}
                        />
                      </div>
                    ))}

                    <div style={{ borderTop: '1px solid var(--color-border-tertiary)', margin: '4px 0' }} />

                    {/* Dynamic lists */}
                    {([
                      { field: 'criterios' as const, label: '✓ Criterios de aceptación', placeholder: 'El sistema valida el formato del email...', color: '#0F6E56' },
                      { field: 'reglas' as const, label: '⊘ Reglas de negocio', placeholder: 'Solo usuarios activos pueden iniciar sesión...', color: '#854F0B' },
                      { field: 'flujos' as const, label: '↳ Flujos alternativos / Excepciones', placeholder: 'Usuario olvida contraseña → mostrar enlace de recuperación...', color: '#534AB7' },
                    ]).map(({ field, label, placeholder, color }) => (
                      <div key={field}>
                        <div style={{ fontSize: 10, fontWeight: 700, color, textTransform: 'uppercase', letterSpacing: '0.05em', marginBottom: 6 }}>{label}</div>
                        <div style={{ display: 'flex', flexDirection: 'column', gap: 4 }}>
                          {guide[field].map((item, i) => (
                            <div key={i} style={{ display: 'flex', gap: 6, alignItems: 'center' }}>
                              <span style={{ color, fontSize: 12, flexShrink: 0 }}>•</span>
                              <input
                                value={item}
                                onChange={e => updateGuideList(field, i, e.target.value)}
                                onKeyDown={e => { if (e.key === 'Enter') { e.preventDefault(); addGuideItem(field); } }}
                                placeholder={placeholder}
                                style={{ flex: 1, padding: '6px 8px', borderRadius: 6, border: '1px solid var(--color-border-secondary)', background: 'var(--color-background-primary)', color: 'var(--color-text-primary)', fontSize: 12, outline: 'none', fontFamily: 'inherit' }}
                              />
                              {guide[field].length > 1 && (
                                <button onClick={() => removeGuideItem(field, i)}
                                  style={{ border: 'none', background: 'transparent', cursor: 'pointer', color: 'var(--color-text-tertiary)', fontSize: 12, padding: '0 2px' }}>✕</button>
                              )}
                            </div>
                          ))}
                          <button onClick={() => addGuideItem(field)}
                            style={{ alignSelf: 'flex-start', padding: '3px 8px', borderRadius: 6, border: `1px dashed ${color}44`, background: 'transparent', color, cursor: 'pointer', fontSize: 10, fontWeight: 600, marginTop: 2 }}>
                            + Agregar
                          </button>
                        </div>
                      </div>
                    ))}

                    <div style={{ borderTop: '1px solid var(--color-border-tertiary)', margin: '4px 0' }} />

                    {/* Notas técnicas */}
                    <div>
                      <div style={{ fontSize: 10, fontWeight: 700, color: 'var(--color-text-tertiary)', textTransform: 'uppercase', letterSpacing: '0.05em', marginBottom: 6 }}>⚙ Notas técnicas (opcional)</div>
                      <textarea
                        value={guide.notas}
                        onChange={e => setGuide(prev => ({ ...prev, notas: e.target.value }))}
                        placeholder={'API: POST /auth/login\nToken JWT, expiración 8 horas'}
                        rows={2}
                        style={{ width: '100%', padding: '7px 8px', borderRadius: 8, border: '1px solid var(--color-border-secondary)', background: 'var(--color-background-primary)', color: 'var(--color-text-primary)', fontSize: 12, resize: 'vertical', boxSizing: 'border-box', fontFamily: 'monospace', outline: 'none' }}
                      />
                    </div>

                    {/* Preview */}
                    {serializeGuide().trim() && (
                      <details style={{ marginTop: 4 }}>
                        <summary style={{ fontSize: 10, color: 'var(--color-text-tertiary)', cursor: 'pointer', userSelect: 'none' }}>Ver contexto que se enviará a la IA ▾</summary>
                        <pre style={{ marginTop: 6, padding: '8px 10px', borderRadius: 6, background: 'var(--color-background-primary)', fontSize: 10, color: 'var(--color-text-secondary)', whiteSpace: 'pre-wrap', fontFamily: 'monospace', lineHeight: 1.5 }}>
                          {serializeGuide()}
                        </pre>
                      </details>
                    )}
                  </div>
                ) : (
                  <textarea
                    value={contextText}
                    onChange={e => setContextText(e.target.value)}
                    placeholder={`Describe el flujo, criterios de aceptación o pega la historia de usuario.\n\nEjemplo: "El usuario debe poder iniciar sesión con email y contraseña. Si las credenciales son incorrectas, mostrar mensaje de error. Bloquear la cuenta tras 5 intentos fallidos."`}
                    rows={5}
                    style={{ width: '100%', padding: '10px 12px', borderRadius: 10, border: '1px solid var(--color-border-secondary)', background: 'var(--color-background-secondary)', color: 'var(--color-text-primary)', fontSize: 13, resize: 'vertical', boxSizing: 'border-box', lineHeight: 1.5, outline: 'none', fontFamily: 'inherit' }}
                  />
                )}

                <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginTop: 6 }}>
                  <span style={{ fontSize: 10, color: 'var(--color-text-tertiary)' }}>
                    {getContextText().length} caracteres · Más contexto = mejores casos
                  </span>
                  <button
                    onClick={handleComplement}
                    disabled={!getContextText().trim() || complementing}
                    style={{
                      padding: '4px 12px', borderRadius: 6, border: 'none',
                      background: getContextText().trim() ? '#7F77DD' : 'var(--color-background-secondary)',
                      color: getContextText().trim() ? '#fff' : 'var(--color-text-tertiary)',
                      fontSize: 11, fontWeight: 600, cursor: getContextText().trim() ? 'pointer' : 'not-allowed',
                      opacity: complementing ? 0.7 : 1,
                    }}
                  >
                    {complementing ? '⟳ Complementando...' : '✦ Complementa'}
                  </button>
                </div>
                {/* Loading overlay mientras la IA complementa el contexto */}
                {complementing && (
                  <div style={{
                    position: 'absolute', inset: 0, borderRadius: 8,
                    background: 'rgba(var(--color-background-primary-rgb, 255,255,255), 0.85)',
                    display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', gap: 8,
                    backdropFilter: 'blur(2px)', zIndex: 10,
                  }}>
                    <div style={{ fontSize: 22, animation: 'spin 1s linear infinite' }}>✦</div>
                    <div style={{ fontSize: 12, fontWeight: 600, color: '#7F77DD' }}>La IA está mejorando tu contexto...</div>
                    <div style={{ fontSize: 11, color: 'var(--color-text-tertiary)' }}>Esto puede tomar unos segundos</div>
                  </div>
                )}
              </div>
            )}

            {/* Screenshot upload */}
            {(mode === 'screenshot' || mode === 'mixed') && (
              <div style={{ marginBottom: 14 }}>
                <label style={{ display: 'block', marginBottom: 6, fontSize: 11, fontWeight: 700, color: 'var(--color-text-secondary)', textTransform: 'uppercase', letterSpacing: '0.05em' }}>
                  Capturas de pantalla ({screenshots.length}/8)
                </label>
                {screenshots.length === 0 ? (
                  <div
                    onDragOver={e => { e.preventDefault(); setIsDraggingOver(true); }}
                    onDragLeave={() => setIsDraggingOver(false)}
                    onDrop={handleDrop}
                    onClick={() => fileInputRef.current?.click()}
                    style={{ padding: '28px', borderRadius: 12, cursor: 'pointer', border: `2px dashed ${isDraggingOver ? '#7F77DD' : 'var(--color-border-secondary)'}`, background: isDraggingOver ? '#EEEDFE44' : 'var(--color-background-secondary)', textAlign: 'center', transition: 'all 0.2s' }}
                  >
                    <div style={{ fontSize: 28, marginBottom: 8 }}>🖼️</div>
                    <div style={{ fontSize: 13, color: 'var(--color-text-secondary)' }}>Arrastra capturas o haz clic para seleccionar</div>
                    <div style={{ fontSize: 11, color: 'var(--color-text-tertiary)', marginTop: 4 }}>PNG, JPG, WebP · Máximo 8 imágenes · 5MB c/u</div>
                  </div>
                ) : (
                  <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(90px, 1fr))', gap: 8 }}>
                    {screenshots.map((s, i) => (
                      <div key={i} style={{ position: 'relative', borderRadius: 8, overflow: 'hidden', border: '1px solid var(--color-border-tertiary)' }}>
                        <img src={s.previewUrl} alt={`Screenshot ${i + 1}`} style={{ width: '100%', aspectRatio: '16/10', objectFit: 'cover', display: 'block' }} />
                        <button onClick={() => removeScreenshot(i)} style={{ position: 'absolute', top: 3, right: 3, width: 18, height: 18, borderRadius: '50%', background: 'rgba(0,0,0,0.65)', color: '#fff', border: 'none', cursor: 'pointer', fontSize: 9, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>✕</button>
                      </div>
                    ))}
                    <div onClick={() => fileInputRef.current?.click()} style={{ aspectRatio: '16/10', borderRadius: 8, border: '2px dashed var(--color-border-secondary)', display: 'flex', alignItems: 'center', justifyContent: 'center', cursor: 'pointer', fontSize: 20, color: 'var(--color-text-tertiary)' }}>+</div>
                  </div>
                )}
                <input ref={fileInputRef} type="file" accept="image/*" multiple onChange={e => e.target.files && addFiles(e.target.files)} style={{ display: 'none' }} />
              </div>
            )}

            {/* Cantidad máxima de casos */}
            <div style={{ marginBottom: 14 }}>
              <label style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 6, fontSize: 11, fontWeight: 700, color: 'var(--color-text-secondary)', textTransform: 'uppercase', letterSpacing: '0.05em' }}>
                <span>Máximo de casos a generar</span>
                <span style={{ color: 'var(--color-text-primary)', fontWeight: 700, textTransform: 'none', letterSpacing: 0 }}>{maxCases}</span>
              </label>
              <input
                type="range" min={3} max={30} step={1} value={maxCases}
                onChange={e => setMaxCases(Number(e.target.value))}
                style={{ width: '100%' }}
              />
              <div style={{ fontSize: 10.5, color: 'var(--color-text-tertiary)', marginTop: 2 }}>
                La IA genera en lotes pequeños hasta cubrir este número (o hasta que ya no encuentre más cobertura relevante) — más casos implica más llamadas y más tiempo de espera.
              </div>
            </div>

            {/* Error */}
            {error && (
              <div style={{ padding: '12px 14px', borderRadius: 10, background: '#FCEBEB', border: '1px solid #F7C1C1', color: '#791F1F', fontSize: 12, marginBottom: 14, lineHeight: 1.5 }}>
                <strong>⚠ Error:</strong> {error}
                {error.includes('API key') && (
                  <div style={{ marginTop: 8, padding: '8px', borderRadius: 6, background: 'rgba(255,255,255,0.5)', fontSize: 11 }}>
                    <strong>Cómo configurar:</strong><br />
                    1. Abre el archivo <code>.env</code> en la raíz del proyecto<br />
                    2. Agrega: <code>AI_API_KEY=tu-api-key-aquí</code><br />
                    3. Puedes usar <code>AI_PROVIDER=openai</code> o <code>AI_PROVIDER=anthropic</code><br />
                    4. Reinicia el backend: <code>docker compose restart backend</code>
                  </div>
                )}
              </div>
            )}

            {/* Generate button */}
            <button
              onClick={generate}
              disabled={loading || (mode !== 'screenshot' && !contextText.trim() && screenshots.length === 0)}
              style={{
                width: '100%', padding: '12px', borderRadius: 10, border: 'none',
                background: loading ? '#b8b4e8' : 'linear-gradient(135deg, #7F77DD, #534AB7)',
                color: '#fff', fontSize: 14, fontWeight: 700,
                cursor: loading ? 'wait' : 'pointer', display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 8, transition: 'opacity 0.2s',
              }}
            >
              {loading ? (
                <>
                  <span style={{ display: 'inline-block', animation: 'spin 1s linear infinite' }}>⟳</span>
                  Analizando con IA...
                </>
              ) : '✦ Generar casos de prueba'}
            </button>

            <div style={{ marginTop: 10, fontSize: 11, color: 'var(--color-text-tertiary)', textAlign: 'center' }}>
              El modelo usará el contexto del escenario "{scenarioTitle}" automáticamente
            </div>

            {/* ── Contexto del sistema ─────────────────────────────────── */}
            {system && (system.description || system.platform) && (
              <div style={{ marginTop: 12, border: '1px solid var(--color-border-tertiary)', borderRadius: 8, overflow: 'hidden' }}>
                <button
                  onClick={() => setSystemExpanded(v => !v)}
                  style={{
                    width: '100%', display: 'flex', alignItems: 'center', justifyContent: 'space-between',
                    padding: '8px 12px', background: 'var(--color-background-secondary)',
                    border: 'none', cursor: 'pointer', fontSize: 12, color: 'var(--color-text-secondary)',
                  }}
                >
                  <span style={{ display: 'flex', alignItems: 'center', gap: 6, fontWeight: 600 }}>
                    🧠 Contexto del sistema
                    {system.platform && (
                      <span style={{
                        fontSize: 10, padding: '2px 7px', borderRadius: 10,
                        background: 'var(--accent)', color: '#fff', fontWeight: 700,
                      }}>
                        {{ web: '🌐 Web', mobile: '📱 Móvil', desktop: '🖥 Desktop', api: '⚡ API' }[system.platform] ?? system.platform}
                      </span>
                    )}
                  </span>
                  <span style={{ fontSize: 10, transition: 'transform 0.2s', display: 'inline-block', transform: systemExpanded ? 'rotate(180deg)' : 'none' }}>▼</span>
                </button>
                {systemExpanded && (
                  <div style={{ padding: '10px 12px', fontSize: 12, color: 'var(--color-text-secondary)', background: 'var(--surface)', lineHeight: 1.6 }}>
                    {system.description
                      ? <p style={{ margin: 0 }}>{system.description}</p>
                      : <p style={{ margin: 0, color: 'var(--color-text-tertiary)', fontStyle: 'italic' }}>Sin descripción. Agrégala en la configuración del sistema para enriquecer la generación IA.</p>
                    }
                    {system.base_url && (
                      <div style={{ marginTop: 6, fontSize: 11, color: 'var(--color-text-tertiary)' }}>
                        🔗 {system.base_url}
                      </div>
                    )}
                  </div>
                )}
              </div>
            )}
          </>
        ) : (
          /* ── Results ─────────────────────────────────────────────────── */
          <div>
            <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 14 }}>
              <div>
                <span style={{ fontSize: 14, fontWeight: 700, color: 'var(--color-text-primary)' }}>
                  {result.cases.length} casos generados
                  {!!result.batches && result.batches > 1 && ` (en ${result.batches} lotes)`}
                </span>
                <span style={{ fontSize: 11, color: 'var(--color-text-tertiary)', marginLeft: 8 }}>
                  Selecciona los que quieres crear
                </span>
              </div>
              <div style={{ display: 'flex', gap: 6 }}>
                <button onClick={() => setSelectedCases(new Set(result.cases.map((_, i) => i)))}
                  style={{ padding: '4px 10px', borderRadius: 6, fontSize: 11, border: '1px solid var(--color-border-secondary)', background: 'transparent', cursor: 'pointer', color: 'var(--color-text-secondary)' }}>
                  Todos
                </button>
                <button onClick={() => setSelectedCases(new Set())}
                  style={{ padding: '4px 10px', borderRadius: 6, fontSize: 11, border: '1px solid var(--color-border-secondary)', background: 'transparent', cursor: 'pointer', color: 'var(--color-text-secondary)' }}>
                  Ninguno
                </button>
              </div>
            </div>

            <div style={{ display: 'flex', flexDirection: 'column', gap: 8, marginBottom: 16 }}>
              {(result.cases as GeneratedCase[]).map((c, i) => {
                const isSelected = selectedCases.has(i);
                const isExpanded = expandedCase === i;
                return (
                  <div key={i} style={{ borderRadius: 10, border: `1px solid ${isSelected ? '#7F77DD' : 'var(--color-border-tertiary)'}`, background: isSelected ? '#EEEDFE11' : 'var(--color-background-secondary)', transition: 'all 0.15s', overflow: 'hidden' }}>
                    {/* Case header */}
                    <div style={{ display: 'flex', alignItems: 'flex-start', gap: 10, padding: '12px 14px', cursor: 'pointer' }}
                      onClick={() => { setSelectedCases(prev => { const next = new Set(prev); isSelected ? next.delete(i) : next.add(i); return next; }); }}>
                      {/* Checkbox */}
                      <div style={{ width: 18, height: 18, borderRadius: 5, flexShrink: 0, marginTop: 1, border: `2px solid ${isSelected ? '#7F77DD' : 'var(--color-border-secondary)'}`, background: isSelected ? '#7F77DD' : 'transparent', display: 'flex', alignItems: 'center', justifyContent: 'center', transition: 'all 0.15s' }}>
                        {isSelected && <span style={{ color: '#fff', fontSize: 10 }}>✓</span>}
                      </div>

                      <div style={{ flex: 1, minWidth: 0 }}>
                        <div style={{ fontSize: 13, fontWeight: 600, color: 'var(--color-text-primary)', marginBottom: 4 }}>{c.title}</div>
                        {c.description && (
                          <div onClick={e => e.stopPropagation()} style={{ fontSize: 11, color: 'var(--color-text-secondary)', lineHeight: 1.4, marginBottom: 6, cursor: 'text' }}>{c.description}</div>
                        )}
                        <div onClick={e => e.stopPropagation()} style={{ display: 'flex', gap: 4, flexWrap: 'wrap', marginBottom: 4, cursor: 'default' }}>
                          {c.criticality && (() => {
                            const CRIT: Record<string, {color: string; bg: string}> = {
                              critical: {color:'#7A1515',bg:'#FCEBEB'}, high: {color:'#7A4515',bg:'#FEF2E7'},
                              medium: {color:'#3B5FAD',bg:'#EBF0FC'}, low: {color:'#0F6E56',bg:'#E1F5EE'},
                            };
                            const cfg = CRIT[c.criticality] ?? CRIT.medium;
                            return <span style={{ padding: '1px 6px', borderRadius: 4, background: cfg.bg, color: cfg.color, fontSize: 9, fontWeight: 700 }}>{c.criticality}</span>;
                          })()}
                          {c.tags.map(tag => (
                            <span key={tag} style={{ padding: '1px 6px', borderRadius: 4, background: '#EEEDFE', color: '#534AB7', fontSize: 9, fontWeight: 600 }}>{tag}</span>
                          ))}
                          {c.steps && c.steps.length > 0 && (
                            <span style={{ padding: '1px 6px', borderRadius: 4, background: 'var(--color-background-primary)', color: 'var(--color-text-tertiary)', fontSize: 9 }}>
                              {c.steps.length} paso{c.steps.length !== 1 ? 's' : ''}
                            </span>
                          )}
                        </div>
                        {(c.preconditions || c.postconditions) && (
                          <div onClick={e => e.stopPropagation()} style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 6, marginTop: 6, cursor: 'text' }}>
                            {c.preconditions && (
                              <div style={{ padding: '5px 8px', borderRadius: 6, background: 'var(--color-background-primary)', border: '1px solid var(--color-border-tertiary)' }}>
                                <div style={{ fontSize: 9, fontWeight: 700, color: 'var(--color-text-tertiary)', textTransform: 'uppercase', marginBottom: 2 }}>Pre</div>
                                <div style={{ fontSize: 10, color: 'var(--color-text-secondary)', lineHeight: 1.4 }}>{c.preconditions}</div>
                              </div>
                            )}
                            {c.postconditions && (
                              <div style={{ padding: '5px 8px', borderRadius: 6, background: 'var(--color-background-primary)', border: '1px solid var(--color-border-tertiary)' }}>
                                <div style={{ fontSize: 9, fontWeight: 700, color: 'var(--color-text-tertiary)', textTransform: 'uppercase', marginBottom: 2 }}>Post</div>
                                <div style={{ fontSize: 10, color: 'var(--color-text-secondary)', lineHeight: 1.4 }}>{c.postconditions}</div>
                              </div>
                            )}
                          </div>
                        )}
                      </div>

                      {/* Expand steps */}
                      {c.steps && c.steps.length > 0 && (
                        <button
                          onClick={e => { e.stopPropagation(); setExpandedCase(isExpanded ? null : i); }}
                          style={{ border: 'none', background: 'transparent', cursor: 'pointer', fontSize: 12, color: 'var(--color-text-tertiary)', padding: '0 4px', transform: isExpanded ? 'rotate(180deg)' : 'none', transition: 'transform 0.2s' }}
                        >
                          ▼
                        </button>
                      )}
                    </div>

                    {/* Expanded steps */}
                    {isExpanded && c.steps && c.steps.length > 0 && (
                      <div style={{ borderTop: '1px solid var(--color-border-tertiary)', padding: '12px 14px', background: 'var(--color-background-primary)' }}>
                        <div style={{ fontSize: 11, fontWeight: 700, color: 'var(--color-text-secondary)', textTransform: 'uppercase', letterSpacing: '0.05em', marginBottom: 10 }}>Pasos generados</div>
                        {c.steps.map((step, si) => (
                          <div key={si} style={{ marginBottom: 10, paddingLeft: 12, borderLeft: '2px solid #CECBF6' }}>
                            <div style={{ fontSize: 12, fontWeight: 600, color: 'var(--color-text-primary)', marginBottom: 3 }}>
                              <span style={{ color: '#7F77DD', marginRight: 6 }}>{si + 1}.</span>{step.description}
                            </div>
                            {step.expected_result && (
                              <div style={{ fontSize: 11, color: 'var(--color-text-secondary)', marginBottom: 4 }}>
                                <span style={{ color: '#1D9E75', fontWeight: 600 }}>✓ </span>{step.expected_result}
                              </div>
                            )}
                            {step.actions && step.actions.length > 0 && (
                              <div style={{ display: 'flex', flexDirection: 'column', gap: 3, marginTop: 4 }}>
                                {step.actions.map((action, ai) => (
                                  <div key={ai} style={{ display: 'flex', alignItems: 'center', gap: 6, padding: '3px 8px', borderRadius: 6, background: 'var(--color-background-secondary)', fontSize: 10 }}>
                                    <span style={{ padding: '1px 5px', borderRadius: 3, background: '#3B8BD422', color: '#185FA5', fontWeight: 700, whiteSpace: 'nowrap' }}>{action.type}</span>
                                    {action.target_selector && <code style={{ color: 'var(--color-text-tertiary)', fontSize: 9 }}>{action.target_selector}</code>}
                                    {action.value && <span style={{ color: 'var(--color-text-secondary)' }}>→ "{action.value}"</span>}
                                  </div>
                                ))}
                              </div>
                            )}
                          </div>
                        ))}
                      </div>
                    )}
                  </div>
                );
              })}
            </div>

            {/* Action buttons */}
            <div style={{ display: 'flex', gap: 8 }}>
              <button onClick={() => { setResult(null); setError(null); }}
                style={{ flex: 1, padding: '10px', borderRadius: 10, border: '1px solid var(--color-border-secondary)', background: 'transparent', cursor: 'pointer', fontSize: 13, color: 'var(--color-text-secondary)', fontWeight: 500 }}>
                ↺ Regenerar
              </button>
              <button
                onClick={saveSelected}
                disabled={selectedCases.size === 0 || saving}
                style={{
                  flex: 2, padding: '10px', borderRadius: 10, border: 'none',
                  background: selectedCases.size === 0 ? 'var(--color-border-secondary)' : 'linear-gradient(135deg, #7F77DD, #534AB7)',
                  color: '#fff', fontSize: 13, fontWeight: 700,
                  cursor: selectedCases.size === 0 ? 'not-allowed' : 'pointer',
                }}
              >
                {saving ? 'Guardando...' : `✓ Crear ${selectedCases.size} caso${selectedCases.size !== 1 ? 's' : ''} seleccionado${selectedCases.size !== 1 ? 's' : ''}`}
              </button>
            </div>
          </div>
        )}
      </div>

      <style>{`@keyframes spin { from { transform: rotate(0deg) } to { transform: rotate(360deg) } }`}</style>

      {/* Complement modal */}
      {complementModal !== null && (
        <div style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.5)', zIndex: 600, display: 'flex', alignItems: 'center', justifyContent: 'center' }}
          onClick={e => e.target === e.currentTarget && setComplementModal(null)}>
          <div style={{ width: 560, maxHeight: '80vh', borderRadius: 16, background: 'var(--color-background-primary)', border: '1px solid var(--color-border-secondary)', display: 'flex', flexDirection: 'column', overflow: 'hidden' }}>
            <div style={{ padding: '16px 20px', borderBottom: '1px solid var(--color-border-tertiary)', display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
              <div>
                <div style={{ fontSize: 15, fontWeight: 700, color: 'var(--color-text-primary)' }}>✦ Contexto complementado</div>
                <div style={{ fontSize: 11, color: 'var(--color-text-tertiary)', marginTop: 2 }}>Revisa y edita antes de usar. El original no se modifica hasta que aceptes.</div>
              </div>
              <button onClick={() => setComplementModal(null)} style={{ border: 'none', background: 'transparent', fontSize: 18, cursor: 'pointer', color: 'var(--color-text-tertiary)' }}>✕</button>
            </div>
            <div style={{ flex: 1, overflowY: 'auto', padding: '16px 20px' }}>
              <textarea
                value={complementEdited}
                onChange={e => setComplementEdited(e.target.value)}
                rows={12}
                style={{ width: '100%', padding: '10px 12px', borderRadius: 10, border: '1px solid var(--color-border-secondary)', background: 'var(--color-background-secondary)', color: 'var(--color-text-primary)', fontSize: 13, resize: 'vertical', boxSizing: 'border-box', lineHeight: 1.6, outline: 'none', fontFamily: 'inherit' }}
              />
            </div>
            <div style={{ padding: '12px 20px', borderTop: '1px solid var(--color-border-tertiary)', display: 'flex', gap: 8, justifyContent: 'flex-end' }}>
              <button onClick={() => setComplementModal(null)}
                style={{ padding: '8px 14px', borderRadius: 8, border: '1px solid var(--color-border-secondary)', background: 'transparent', cursor: 'pointer', fontSize: 12, color: 'var(--color-text-secondary)' }}>
                ✕ Descartar
              </button>
              <button onClick={() => { setContextText(complementEdited); setComplementModal(null); }}
                style={{ padding: '8px 18px', borderRadius: 8, border: 'none', background: '#7F77DD', color: '#fff', cursor: 'pointer', fontSize: 12, fontWeight: 600 }}>
                ✓ Usar este contexto
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}